import type {
	PaymentLinkEntity,
	PaymentLinkType,
} from '../models/entity/payment-link.entity';

/**
 * PD-16674 — rules for payment links that both sides need: how long a link
 * lives, when it stops working, where it points, how an amount is checked and
 * how a statement payment is spread over the invoices it covers.
 *
 * Pure and isomorphic, so the server's decisions and what the storefront page
 * shows come from the same code. Token generation and hashing need Node's
 * crypto and live in the crm-api.
 */

/**
 * The request header the storefront sends a link's token in. The token never
 * goes in an API URL: request paths are stored by the API's request and
 * access logs, and this header is redacted from both.
 */
export const PAYMENT_LINK_TOKEN_HEADER = 'X-Payment-Link-Token';

/** The Turnstile `action` each public page renders its widget with. */
export const PAYMENT_LINK_TURNSTILE_ACTIONS: Readonly<
	Record<'pay' | 'savePaymentMethod' | 'sentForm', string>
> = {
	pay: 'payment-link-pay',
	savePaymentMethod: 'payment-method-link',
	// PD-13768 — adding a card or bank account on a sent form, and signing it.
	sentForm: 'sent-form',
};

/**
 * Days a link works for, counted from when it was issued.
 *
 * An invoice or statement link lasts 90 days: it rides in an invoice or
 * statement email a customer may act on weeks later, and it stops by itself
 * once nothing it covers is owed. A payment-method link lasts 7 days: it is
 * sent for a conversation that is happening now, and a card-saving page left
 * open for months is exposure with no use.
 */
export const PAYMENT_LINK_EXPIRY_DAYS: Readonly<Record<PaymentLinkType, number>> =
	{
		invoice: 90,
		statement: 90,
		payment_method: 7,
		// PD-13768 — the default for a sent form; each form sets its own expiry
		// (`form.sendSettings.linkExpiryDays`), which the issuer passes instead.
		form: 30,
	};

/**
 * Charges or saves the gateway may refuse through one link before the link
 * locks. A real customer mistypes a card once or twice; a script testing
 * stolen card numbers needs many tries, and a lock ends that at five.
 */
export const PAYMENT_LINK_MAX_FAILED_ATTEMPTS = 5;

/**
 * A token as issued: 32 random bytes in base64url, which is always 43
 * characters. Anything else is refused before a database lookup.
 */
const PAYMENT_LINK_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export function isWellFormedPaymentLinkToken(value: unknown): value is string {
	return typeof value === 'string' && PAYMENT_LINK_TOKEN_PATTERN.test(value);
}

export function computePaymentLinkExpiry(
	type: PaymentLinkType,
	issuedAt: Date,
): Date {
	return new Date(
		issuedAt.getTime() + PAYMENT_LINK_EXPIRY_DAYS[type] * 24 * 60 * 60 * 1000,
	);
}

/**
 * Whether a link can still be used, from its own row alone. Revoked wins over
 * locked, and locked over expired, so the page tells the customer the most
 * useful thing: a revoked link was withdrawn on purpose, a locked one needs a
 * new link, an expired one simply ran out. Whether anything is still OWED is
 * a separate question the server answers from the live invoices.
 */
export type PaymentLinkLifecycleState =
	| 'active'
	| 'expired'
	| 'revoked'
	| 'locked';

export function resolvePaymentLinkLifecycleState(
	link: Pick<
		PaymentLinkEntity,
		'expiresAt' | 'revokedAt' | 'lockedAt' | 'failedAttemptCount'
	>,
	now: Date,
): PaymentLinkLifecycleState {
	if (link.revokedAt != null) {
		return 'revoked';
	}
	if (
		link.lockedAt != null ||
		link.failedAttemptCount >= PAYMENT_LINK_MAX_FAILED_ATTEMPTS
	) {
		return 'locked';
	}
	if (new Date(link.expiresAt).getTime() <= now.getTime()) {
		return 'expired';
	}
	return 'active';
}

/**
 * The storefront path a link opens. Invoice and statement links share one
 * page; the page reads the link to know which it is.
 */
export function paymentLinkPath(type: PaymentLinkType, token: string): string {
	if (type === 'form') {
		// PD-13768 — a sent form's page.
		return `/form/${token}`;
	}
	return type === 'payment_method'
		? `/payment-method/${token}`
		: `/pay/${token}`;
}

/**
 * PD-16674 — the token in a link built by {@link buildPaymentLinkUrl}, or null
 * when the text is not a payment link. The CRM's Send dialog shows a link and
 * can then email that same link, so the server reads the token back out of the
 * URL it handed the dialog.
 */
export function paymentLinkTokenFromUrl(url: string): string | null {
	let pathname: string;
	try {
		pathname = new URL(url).pathname;
	} catch {
		return null;
	}
	const token = /^\/(?:pay|payment-method|form)\/([^/]+)\/?$/.exec(pathname)?.[1];
	return isWellFormedPaymentLinkToken(token) ? token : null;
}

/** The full link: the storefront origin (`https://host`) plus the path. */
export function buildPaymentLinkUrl(
	storefrontOrigin: string,
	type: PaymentLinkType,
	token: string,
): string {
	return `${storefrontOrigin.replace(/\/+$/, '')}${paymentLinkPath(type, token)}`;
}

export type PaymentLinkAmountValidationModel =
	| { ok: true; amount: number }
	| {
			ok: false;
			reason: 'invalid' | 'not_positive' | 'exceeds_balance' | 'too_precise';
	  };

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Checks the amount a payer asked to pay against what is still owed. It must
 * be a finite number, more than zero, in whole cents, and no more than the
 * open balance; anything else is refused rather than corrected, because the
 * payer should pay what they typed or be told why not.
 *
 * Half a cent of float noise is tolerated in both checks (89.99999999 is
 * 90.00), and the amount comes back rounded to cents.
 */
export function validatePaymentLinkAmount(
	requested: unknown,
	openBalance: number,
): PaymentLinkAmountValidationModel {
	if (typeof requested !== 'number' || !Number.isFinite(requested)) {
		return { ok: false, reason: 'invalid' };
	}
	const amount = round2(requested);
	if (Math.abs(amount - requested) > 0.000001) {
		return { ok: false, reason: 'too_precise' };
	}
	if (amount <= 0) {
		return { ok: false, reason: 'not_positive' };
	}
	if (amount > round2(openBalance) + 0.005) {
		return { ok: false, reason: 'exceeds_balance' };
	}
	return { ok: true, amount };
}

export interface PaymentLinkOpenInvoiceModel {
	invoiceId: number;
	/** What is still owed on it now, as the server measured it. */
	balance: number;
}

export type PaymentLinkAllocationValidationModel =
	| { ok: true; allocations: Array<{ invoiceId: number; amount: number }> }
	| {
			ok: false;
			reason:
				| 'empty'
				| 'unknown_invoice'
				| 'duplicate_invoice'
				| 'not_positive'
				| 'too_precise'
				| 'exceeds_invoice_balance'
				| 'total_mismatch';
	  };

/**
 * PD-16674 — checks the per-invoice amounts a statement-link payer chose:
 * each line names an invoice the link still covers, once, for a positive
 * dollars-and-cents amount no larger than that invoice's balance, and the
 * lines add up to the amount being paid. Lines are returned in the link's
 * order (oldest due first), so the billing engine applies them as it would
 * an automatic split.
 */
export function validatePaymentLinkAllocations(
	lines: ReadonlyArray<{ invoiceId: unknown; amount: unknown }>,
	invoices: ReadonlyArray<PaymentLinkOpenInvoiceModel>,
	amount: number,
): PaymentLinkAllocationValidationModel {
	if (lines.length === 0) {
		return { ok: false, reason: 'empty' };
	}
	const balanceById = new Map(
		invoices.map((invoice) => [invoice.invoiceId, round2(invoice.balance)]),
	);
	const chosen = new Map<number, number>();
	for (const line of lines) {
		if (typeof line.invoiceId !== 'number' || !balanceById.has(line.invoiceId)) {
			return { ok: false, reason: 'unknown_invoice' };
		}
		if (chosen.has(line.invoiceId)) {
			return { ok: false, reason: 'duplicate_invoice' };
		}
		if (typeof line.amount !== 'number' || !Number.isFinite(line.amount)) {
			return { ok: false, reason: 'not_positive' };
		}
		const lineAmount = round2(line.amount);
		if (Math.abs(lineAmount - line.amount) > 0.000001) {
			return { ok: false, reason: 'too_precise' };
		}
		if (lineAmount <= 0) {
			return { ok: false, reason: 'not_positive' };
		}
		if (lineAmount > (balanceById.get(line.invoiceId) ?? 0) + 0.005) {
			return { ok: false, reason: 'exceeds_invoice_balance' };
		}
		chosen.set(line.invoiceId, lineAmount);
	}
	const total = round2(
		Array.from(chosen.values()).reduce((sum, value) => sum + value, 0),
	);
	if (Math.abs(total - round2(amount)) > 0.005) {
		return { ok: false, reason: 'total_mismatch' };
	}
	return {
		ok: true,
		allocations: invoices
			.filter((invoice) => chosen.has(invoice.invoiceId))
			.map((invoice) => ({
				invoiceId: invoice.invoiceId,
				amount: chosen.get(invoice.invoiceId) ?? 0,
			})),
	};
}

/**
 * Spreads a payment over the invoices a link covers, in the order given
 * (oldest due first), filling each before moving to the next. This is the
 * plan handed to the billing engine, which re-measures every line against the
 * live invoice anyway; an amount larger than the total is capped at it.
 * Invoices with nothing owed are skipped.
 */
export function allocatePaymentLinkAmount(
	amount: number,
	invoices: ReadonlyArray<PaymentLinkOpenInvoiceModel>,
): Array<{ invoiceId: number; amount: number }> {
	const allocations: Array<{ invoiceId: number; amount: number }> = [];
	let remaining = round2(Math.max(amount, 0));
	for (const invoice of invoices) {
		if (remaining <= 0.005) {
			break;
		}
		const balance = round2(invoice.balance);
		if (balance <= 0.005) {
			continue;
		}
		const share = round2(Math.min(remaining, balance));
		allocations.push({ invoiceId: invoice.invoiceId, amount: share });
		remaining = round2(remaining - share);
	}
	return allocations;
}
